<script>
import axios from 'axios'
import http from '../../../utils/services/http'

/**
 * Janela da campanha de MIGRAÇÃO para o novo ERP V5 (2026).
 *
 * Duas abas:
 *  1. "Oferta de migração" — apelo comercial (migração para o plano Premium,
 *     R$ 2.900,00, vagas por ordem de contratação). O CTA gera um PIX via API
 *     legada (POST /api/public/migracao/pix) na conta Asaas MASTER da SL.
 *  2. "Conheça o novo ERP" — o anúncio original ("depois de 2 anos... chegou!").
 *
 * Segmentação: aparece para TODOS os clientes logados, exceto os domínios listados
 * em EXCECOES (esses serão tratados caso a caso com janelas individuais).
 *
 * Dispensa: só some de vez se marcar "Não quero mais ver este aviso" (cookie 365d).
 * Fechar sem marcar → volta a aparecer no próximo carregamento.
 *
 * Identidade visual do site novo (suporteleiloes.com.br):
 *  ink #050816 / blue #2563EB / amber #F59E0B, cantos arredondados, glow.
 */

// Cookie PRÓPRIO desta campanha (2026) — NÃO reusar o do modal antigo
// ('sl_aviso_novo_erp'), senão quem já dispensou o aviso antigo nunca veria esta
// oferta. Assim, todos voltam a ver a janela (até dispensarem esta especificamente).
const COOKIE_NAME = 'sl_oferta_migracao_2026'
const NEVER_DAYS = 365

// Clientes (domínio-base) que NÃO veem a oferta genérica — tratados
// individualmente (janelas específicas). Comparação normalizada: sem erp./www.
// e sem porta, então cobre qualquer subdomínio do cliente.
const EXCECOES = [
  'kleiloes.com.br',
  'goldenlance.com.br',
  'saraivaleiloes.com.br',
  'peterlongoleiloes.com.br',
  'rogeriomenezes.com.br',
  'webleiloes.com.br',       // vê o ModalCobranca (cobrança), não a oferta de migração
]

// Endpoint da API legada (mesma baseURL do app, :8000) que gera o PIX no Asaas.
const PIX_ENDPOINT = '/api/public/migracao/pix'

// Vídeo de apresentação (YouTube/Vimeo/.mp4). Vazio = placeholder.
const VIDEO_URL = 'https://vimeo.com/1201827234'

function hostname() {
  return (typeof window !== 'undefined' && window.location && window.location.hostname) || ''
}
// Domínio-base do cliente: sem porta e sem prefixo erp./www.
function clienteBase(host) {
  return String(host || '').replace(/:\d+$/, '').replace(/^(erp|www)\./i, '').toLowerCase()
}
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}
function setCookie(name, value, days) {
  const d = new Date()
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`
}

export default {
  name: 'ModalNovoErp',
  data() {
    return {
      opened: false,
      tab: 'oferta',            // 'oferta' | 'novoErp'
      naoMostrarMais: false,
      videoUrl: VIDEO_URL,
      // Estado do pagamento: idle → loading → done | error
      payStatus: 'idle',
      pix: null,                // { payload, encodedImage, expirationDate }
      payError: '',
      copied: false,
      explica: false,           // toggle do "Por que o custo da migração?"
    }
  },
  computed: {
    videoEmbed() {
      const url = (this.videoUrl || '').trim()
      if (!url) return null
      let m
      if ((m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/))) {
        return { type: 'iframe', src: `https://www.youtube.com/embed/${m[1]}?rel=0&modestbranding=1` }
      }
      if ((m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/))) {
        const h = (url.match(/[?&]h=([\w]+)/) || [])[1]
        const params = `badge=0&autopause=0&dnt=1${h ? `&h=${h}` : ''}`
        return { type: 'iframe', src: `https://player.vimeo.com/video/${m[1]}?${params}` }
      }
      if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)) {
        return { type: 'video', src: url }
      }
      return { type: 'iframe', src: url }
    },
    qrDataUri() {
      const img = this.pix && this.pix.encodedImage
      if (!img) return null
      return img.startsWith('data:') ? img : `data:image/png;base64,${img}`
    },
  },
  mounted() {
    // Escapa da árvore de componentes pra ficar acima de tudo (igual Autorizador).
    document.body.append(this.$el)
    if (getCookie(COOKIE_NAME)) return
    if (EXCECOES.indexOf(clienteBase(hostname())) !== -1) return   // tratado individualmente
    this.$openTimer = setTimeout(() => {
      this.opened = true
      this.track('novo-erp-modal/abrir')
    }, 1400)
  },
  beforeDestroy() {
    this.$openTimer && clearTimeout(this.$openTimer)
    document.body.contains(this.$el) && document.body.removeChild(this.$el)
  },
  methods: {
    track(evt) {
      try {
        const session = this.$uloc && this.$uloc.auth && this.$uloc.auth.session
        const userId = session && session.user && session.user.id
        typeof SLEventClient === 'function' && SLEventClient('click', 'erp', evt, hostname(), userId)
      } catch (e) { /* noop */ }
    },
    persistDismiss() {
      if (this.naoMostrarMais) setCookie(COOKIE_NAME, 'never', NEVER_DAYS)
    },
    fechar() {
      this.persistDismiss()
      this.opened = false
    },
    irPara(tab) {
      this.tab = tab
      this.track('novo-erp-modal/aba/' + tab)
    },
    // Gera o PIX de migração via API legada (conta Asaas master da SL).
    async reservar() {
      if (this.payStatus === 'loading') return
      this.track('novo-erp-modal/reservar')
      this.payStatus = 'loading'
      this.payError = ''
      try {
        // Chamada ANÔNIMA (sem o JWT do tenant). Em produção, enviar o Bearer do
        // tenant junto com uloc-mi: gerencia faz o backend rejeitar com 401
        // "Invalid User Client Session <tenant>/gerencia". O endpoint é público e
        // roteia pra gerência pelo próprio header uloc-mi. Usamos um axios limpo
        // (sem Authorization e sem o interceptor que desloga em 401).
        const base = (http && http.defaults && http.defaults.baseURL) || ''
        const bare = axios.create({ baseURL: base })
        const resp = await bare.post(PIX_ENDPOINT, { dominio: hostname() }, { headers: { 'uloc-mi': 'gerencia' } })
        let data = resp && resp.data
        if (typeof data === 'string') {
          try { data = JSON.parse(data) } catch (e) { /* mantém string */ }
        }
        // Cliente já pagou (idempotência do backend) → tela de agradecimento.
        if (data && data.status === 'paid') {
          this.payStatus = 'paid'
          return
        }
        const pix = data && data.pix
        if (!pix || !pix.payload) {
          throw new Error((data && data.error) || 'Resposta sem dados de PIX.')
        }
        this.pix = pix
        this.payStatus = 'done'
      } catch (e) {
        let body = e && e.response && e.response.data
        if (typeof body === 'string') {
          try { body = JSON.parse(body) } catch (err) { /* mantém string */ }
        }
        const msg = (body && (body.message || body.error)) ||
          (e && e.message) || 'Falha ao gerar o PIX. Tente novamente.'
        this.payError = (typeof msg === 'string' && msg) ? msg : 'Falha ao gerar o PIX. Tente novamente.'
        this.payStatus = 'error'
      }
    },
    async copiar() {
      const code = this.pix && this.pix.payload
      if (!code) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(code)
        } else {
          const ta = document.createElement('textarea')
          ta.value = code
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copied = true
        setTimeout(() => { this.copied = false }, 2200)
      } catch (e) { /* noop */ }
    },
    tentarNovamente() {
      this.payStatus = 'idle'
      this.payError = ''
    },
  },
}
</script>

<template>
  <div class="snerp" v-if="opened">
    <div class="snerp__overlay" @click="fechar"></div>

    <div class="snerp__card" role="dialog" aria-modal="true" aria-labelledby="snerp-title">
      <button class="snerp__x" type="button" aria-label="Fechar" @click="fechar">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>

      <div class="snerp__glow" aria-hidden="true"></div>

      <!-- Abas -->
      <div class="snerp__tabs" role="tablist">
        <button class="snerp__tab" :class="{ 'is-active': tab === 'oferta' }" type="button" role="tab" :aria-selected="tab === 'oferta'" @click="irPara('oferta')">
          <span class="snerp__tab-dot"></span> Oferta de migração
        </button>
        <button class="snerp__tab" :class="{ 'is-active': tab === 'novoErp' }" type="button" role="tab" :aria-selected="tab === 'novoErp'" @click="irPara('novoErp')">
          Conheça o novo ERP
        </button>
      </div>

      <!-- ABA 1: OFERTA DE MIGRAÇÃO -->
      <div v-show="tab === 'oferta'" class="snerp__body">
        <template v-if="payStatus !== 'done' && payStatus !== 'paid'">
          <div class="snerp__urgency" role="alert">
            <span class="snerp__urgency-pulse" aria-hidden="true"></span>
            <span class="snerp__urgency-text">O prazo com desconto <strong>encerrou</strong> — mas a migração está aberta, e as vagas correm por <strong>ordem de contratação</strong>.</span>
          </div>

          <span class="snerp__badge snerp__badge--amber">
            <span class="snerp__badge-dot snerp__badge-dot--amber"></span> Migração aberta · vagas por ordem de contratação
          </span>

          <h2 id="snerp-title" class="snerp__title">
            Chegou a hora de migrar para a <span class="snerp__hl">ferramenta de leilões mais poderosa do mercado</span> 🚀
          </h2>

          <p class="snerp__text">
            Reescrito <strong>do zero para a próxima década</strong> e <strong>muito mais rápido</strong>:
            <strong class="snerp__ia">IA nativa</strong>, <strong>CRM completo</strong>,
            <strong>WhatsApp integrado</strong>, <strong>marketing e captação de leads</strong>,
            <strong>atendimento omnichannel</strong>, <strong>financeiro integrado</strong>,
            <strong>novos aplicativos</strong> e <strong>milhares de novas funcionalidades</strong>
            — tudo que você já conhece, agora melhor.
          </p>

          <p class="snerp__solve">
            <span class="snerp__solve-ico">✨</span>
            <span>A nova versão foi feita para <strong>resolver de vez os nossos problemas de suporte e atendimento</strong>.</span>
          </p>

          <ul class="snerp__list">
            <li><span class="snerp__check">✓</span> Migração para o plano <strong>Premium</strong> — com opção de <strong>upgrade para o Premium IA</strong> quando quiser, direto pelo software</li>
            <li><span class="snerp__check">✓</span> <strong>Vagas por ordem de contratação:</strong> quanto antes você reservar, antes acontecem seu <strong>treinamento, ambiente de testes e homologação</strong></li>
            <li><span class="snerp__check">✓</span> Migração <strong>planejada com você</strong>: homologação, treinamento da equipe e só então a virada, com calma e segurança</li>
          </ul>

          <p class="snerp__reassure">
            Ao contratar, você <strong>garante sua posição na fila de migração</strong>. A migração é
            conduzida no seu ritmo, alinhada com a sua equipe — nada muda no seu sistema agora.
          </p>

          <div class="snerp__price">
            <div class="snerp__price-label">Valor único da migração</div>
            <div class="snerp__price-value">R$ 2.900<span class="snerp__price-cents">,00</span></div>
            <div class="snerp__price-note">pagamento via PIX · migração para o plano <strong>Premium</strong>, com upgrade opcional para o Premium IA</div>
          </div>

          <button type="button" class="snerp__why" :class="{ 'is-open': explica }" :aria-expanded="explica ? 'true' : 'false'" @click="explica = !explica">
            <span>Por que o custo da migração?</span>
            <svg class="snerp__why-chev" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <p v-show="explica" class="snerp__why-text">
            Esta nova versão precisa de uma <strong>migração do zero de toda a sua base de dados</strong>.
            Além disso, subimos <strong>servidores exclusivos para você</strong>: um <strong>IA Worker</strong>
            para controle da inteligência artificial, um <strong>servidor de e-mail marketing dedicado</strong>
            para envios automatizados com políticas individuais, o <strong>treinamento do zero</strong> da sua
            equipe e vários <strong>serviços de integração</strong> necessários para a migração.
          </p>

          <button class="snerp__cta" type="button" :disabled="payStatus === 'loading'" @click="reservar">
            <template v-if="payStatus === 'loading'">
              <span class="snerp__spin" aria-hidden="true"></span> Gerando seu PIX…
            </template>
            <template v-else>
              Quero reservar minha vaga e agendar meu treinamento
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </template>
          </button>

          <p v-if="payStatus === 'error'" class="snerp__error">
            {{ payError }} <button type="button" class="snerp__link" @click="tentarNovamente">Tentar novamente</button>
          </p>
        </template>

        <!-- Estado: PIX gerado -->
        <template v-else-if="payStatus === 'done'">
          <span class="snerp__badge">
            <span class="snerp__badge-dot"></span> Reserva iniciada · pague via PIX
          </span>
          <h2 class="snerp__title snerp__title--sm">Falta pouco! Pague o PIX para garantir sua vaga</h2>
          <p class="snerp__text">
            Escaneie o QR Code ou use o <strong>copia e cola</strong> abaixo. Assim que o pagamento de
            <strong>R$ 2.900,00</strong> cair, entramos em contato para agendar sua migração e treinamento.
          </p>

          <div class="snerp__pix">
            <img v-if="qrDataUri" :src="qrDataUri" alt="QR Code PIX" class="snerp__qr" />
            <div class="snerp__pix-copy">
              <label class="snerp__pix-label">PIX copia e cola</label>
              <textarea class="snerp__pix-code" readonly rows="4" :value="pix.payload" @focus="$event.target.select()"></textarea>
              <button class="snerp__copy" type="button" @click="copiar">
                {{ copied ? '✓ Copiado!' : 'Copiar código PIX' }}
              </button>
            </div>
          </div>
        </template>

        <!-- Estado: pagamento já identificado -->
        <template v-else>
          <span class="snerp__badge">
            <span class="snerp__badge-dot"></span> Pagamento identificado
          </span>
          <h2 class="snerp__title snerp__title--sm">Recebemos seu pagamento — obrigado! 🎉</h2>
          <p class="snerp__text">
            Sua <strong>reserva de migração já está confirmada</strong>. Nossa equipe vai entrar em
            contato para agendar a homologação e o treinamento. <strong>Não é preciso pagar de novo.</strong>
          </p>
        </template>
      </div>

      <!-- ABA 2: CONHEÇA O NOVO ERP (anúncio original) -->
      <div v-show="tab === 'novoErp'" class="snerp__body">
        <span class="snerp__badge">
          <span class="snerp__badge-dot"></span> Novo ERP · 2026
        </span>

        <h2 class="snerp__title">
          Nosso novo ERP, depois de 2 anos de desenvolvimento, <span class="snerp__hl">chegou!</span> 🚀
        </h2>

        <p class="snerp__text">
          Juntamos <strong>10 anos de experiência</strong> em erros e acertos do nosso antigo ERP e
          reescrevemos um sistema <strong>do zero</strong> para solucionar e evoluir tudo que sempre
          imaginamos, aproveitando o que temos de mais moderno hoje: <strong class="snerp__ia">IA</strong>.
        </p>
        <p class="snerp__text">
          A migração será planejada <strong>cliente por cliente a partir de 20 de julho de 2026</strong>,
          mas você pode antecipar e ser um dos primeiros a utilizar todo o poder da nossa tecnologia.
        </p>

        <div class="snerp__video">
          <template v-if="videoEmbed">
            <iframe
              v-if="videoEmbed.type === 'iframe'"
              :src="videoEmbed.src"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
            <video v-else :src="videoEmbed.src" controls playsinline></video>
          </template>
          <div v-else class="snerp__video-ph">
            <div class="snerp__play" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            </div>
            <span class="snerp__video-label">Vídeo de apresentação · em breve</span>
          </div>
        </div>

        <button class="snerp__cta snerp__cta--ghost" type="button" @click="irPara('oferta')">
          Ver oferta de migração
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </button>

        <a class="snerp__doclink" href="https://docs.suporteleiloes.com" target="_blank" rel="noopener" @click="track('novo-erp-modal/docs')">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
          Quer conhecer a fundo? Veja a documentação completa em <strong>docs.suporteleiloes.com</strong>
        </a>
      </div>

      <label class="snerp__dismiss">
        <input type="checkbox" v-model="naoMostrarMais" />
        <span>Não quero mais ver este aviso</span>
      </label>
    </div>
  </div>
</template>

<style scoped>
.snerp {
  position: fixed;
  inset: 0;
  z-index: 12050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}
.snerp__overlay {
  position: absolute;
  inset: 0;
  background: rgba(5, 8, 22, .72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: snerp-fade .25s ease;
}
.snerp__card {
  position: relative;
  width: 90vw;
  height: 90vh;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 24px;
  border: 1px solid rgba(138, 180, 251, .18);
  background:
    radial-gradient(ellipse 80% 50% at 50% -10%, rgba(37, 99, 235, .35), transparent 60%),
    radial-gradient(ellipse 60% 50% at 85% 15%, rgba(245, 158, 11, .14), transparent 60%),
    linear-gradient(180deg, #0B1224 0%, #050816 100%);
  box-shadow:
    0 30px 80px -20px rgba(0, 0, 0, .7),
    0 0 0 1px rgba(255, 255, 255, .03) inset;
  animation: snerp-pop .32s cubic-bezier(.22, 1, .36, 1);
}
.snerp__glow {
  position: absolute;
  top: -1px; left: 10%; right: 10%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(138, 180, 251, .7), transparent);
}
.snerp__x {
  position: absolute;
  top: 14px; right: 14px;
  width: 34px; height: 34px;
  display: inline-flex; align-items: center; justify-content: center;
  border: none; border-radius: 50%;
  background: rgba(255, 255, 255, .06);
  color: rgba(255, 255, 255, .65);
  cursor: pointer;
  transition: background .15s, color .15s;
  z-index: 3;
}
.snerp__x:hover { background: rgba(255, 255, 255, .12); color: #fff; }

/* Abas */
.snerp__tabs {
  display: flex;
  gap: 6px;
  padding: 16px clamp(20px, 5vw, 40px) 0;
  padding-right: 60px;
}
.snerp__tab {
  display: inline-flex; align-items: center; gap: 7px;
  border: none;
  background: transparent;
  color: rgba(148, 163, 184, .8);
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: .01em;
  padding: 9px 14px;
  border-radius: 10px 10px 0 0;
  cursor: pointer;
  transition: color .15s, background .15s;
}
.snerp__tab:hover { color: #cbd5e1; }
.snerp__tab.is-active {
  color: #fff;
  background: rgba(37, 99, 235, .16);
  box-shadow: inset 0 -2px 0 #2563EB;
}
.snerp__tab-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #F59E0B;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, .2);
}

.snerp__body {
  padding: 24px clamp(24px, 5vw, 44px) 20px;
  max-width: 1152px;
  margin: 0 auto;
}

/* Funcionalidades em texto — palavras-chave destacadas na cor da marca */
.snerp__text strong { color: #fff; }
.snerp__text .snerp__ia { color: #FBBF24 !important; }

/* Callout de impacto: resolve suporte e atendimento */
.snerp__solve {
  display: flex; align-items: center; gap: 11px;
  margin: 0 0 18px;
  padding: 12px 15px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(37, 99, 235, .18), rgba(34, 197, 94, .1));
  border: 1px solid rgba(124, 168, 247, .32);
  font-size: 14.5px; line-height: 1.45;
  color: #e6eefc;
}
.snerp__solve-ico { flex: none; font-size: 18px; }
.snerp__solve strong { color: #fff; font-weight: 700; }

.snerp__reassure {
  font-size: 13.5px;
  line-height: 1.55;
  color: rgba(148, 163, 184, .92);
  background: rgba(37, 99, 235, .08);
  border-left: 3px solid rgba(124, 168, 247, .5);
  border-radius: 0 10px 10px 0;
  padding: 11px 14px;
  margin: 0 0 20px;
}
.snerp__reassure strong { color: #cfe0ff; }

/* Barra de urgência + contador regressivo */
.snerp__urgency {
  display: flex; align-items: center; gap: 12px;
  padding: 11px 16px;
  margin-bottom: 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(220, 38, 38, .22), rgba(245, 158, 11, .16));
  border: 1px solid rgba(248, 113, 113, .4);
}
.snerp__urgency-pulse {
  flex: none;
  width: 10px; height: 10px; border-radius: 50%;
  background: #f87171;
  box-shadow: 0 0 0 0 rgba(248, 113, 113, .6);
  animation: snerp-pulse 1.4s infinite;
}
.snerp__urgency-text {
  flex: 1 1 auto;
  font-size: 14px;
  color: #fde2e2;
  letter-spacing: .01em;
}
.snerp__urgency-text strong { color: #fff; font-weight: 800; }
.snerp__countdown {
  flex: none;
  display: inline-flex; align-items: baseline;
  font-family: 'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace;
  font-size: 20px; font-weight: 800; letter-spacing: .02em;
  color: #fff;
  background: rgba(5, 8, 22, .5);
  border: 1px solid rgba(248, 113, 113, .35);
  border-radius: 9px;
  padding: 5px 11px;
  font-variant-numeric: tabular-nums;
}
.snerp__countdown i { color: #f87171; font-style: normal; padding: 0 1px; }
@keyframes snerp-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(248, 113, 113, .55); }
  70%  { box-shadow: 0 0 0 9px rgba(248, 113, 113, 0); }
  100% { box-shadow: 0 0 0 0 rgba(248, 113, 113, 0); }
}
.snerp__badge {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
  color: #8AB4FB;
  background: rgba(37, 99, 235, .14);
  border: 1px solid rgba(124, 168, 247, .28);
  border-radius: 999px;
  padding: 5px 13px;
  margin-bottom: 16px;
}
.snerp__badge--amber {
  color: #FBBF24;
  background: rgba(245, 158, 11, .12);
  border-color: rgba(245, 158, 11, .32);
}
.snerp__badge-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, .22);
}
.snerp__badge-dot--amber {
  background: #FBBF24;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, .22);
}
.snerp__title {
  font-size: clamp(22px, 3.2vw, 29px);
  line-height: 1.18;
  font-weight: 800;
  color: #fff;
  margin: 0 0 14px;
  letter-spacing: -.01em;
}
.snerp__title--sm { font-size: clamp(19px, 2.6vw, 23px); }
.snerp__hl {
  background: linear-gradient(135deg, #5C8DF6, #8AB4FB);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.snerp__hl-amber {
  background: linear-gradient(135deg, #FBBF24, #F59E0B);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.snerp__text {
  font-size: 15px;
  line-height: 1.62;
  color: rgba(226, 232, 240, .82);
  margin: 0 0 14px;
}
.snerp__text strong { color: #fff; font-weight: 600; }
.snerp__ia { color: #FBBF24 !important; font-weight: 700 !important; }

/* Lista de benefícios */
.snerp__list {
  list-style: none;
  margin: 4px 0 20px;
  padding: 0;
  display: flex; flex-direction: column; gap: 10px;
}
.snerp__list li {
  display: flex; align-items: flex-start; gap: 10px;
  font-size: 14.5px;
  line-height: 1.5;
  color: rgba(226, 232, 240, .9);
}
.snerp__list strong { color: #fff; }
.snerp__check {
  flex: none;
  width: 20px; height: 20px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: rgba(34, 197, 94, .16);
  color: #4ade80;
  font-size: 12px; font-weight: 800;
  margin-top: 1px;
}

/* Bloco de preço */
.snerp__price {
  text-align: center;
  border: 1px solid rgba(245, 158, 11, .28);
  border-radius: 16px;
  background:
    radial-gradient(ellipse 70% 80% at 50% 0%, rgba(245, 158, 11, .12), transparent 70%),
    rgba(255, 255, 255, .02);
  padding: 18px 16px;
  margin-bottom: 20px;
}
.snerp__price-label {
  font-size: 12.5px; font-weight: 600; letter-spacing: .02em;
  color: rgba(203, 213, 225, .85);
  margin-bottom: 4px;
}
.snerp__price-value {
  font-size: 42px; font-weight: 800; line-height: 1;
  color: #fff;
  letter-spacing: -.02em;
}
.snerp__price-cents { font-size: 22px; font-weight: 700; color: rgba(255,255,255,.75); }
.snerp__price-note {
  font-size: 12.5px;
  color: rgba(148, 163, 184, .85);
  margin-top: 8px;
}
.snerp__price-warn {
  font-size: 13px;
  font-weight: 600;
  color: #fca5a5;
  margin-top: 10px;
}
.snerp__price-warn strong { color: #fff; }

/* Explicativo expansível "Por que o custo da migração?" */
.snerp__why {
  display: flex; align-items: center; gap: 8px;
  width: 100%;
  margin: 0 0 6px;
  padding: 10px 2px;
  background: none; border: none;
  color: #8AB4FB;
  font-family: inherit; font-size: 13.5px; font-weight: 700;
  cursor: pointer;
  text-align: left;
}
.snerp__why:hover { color: #a9c6ff; }
.snerp__why-chev { transition: transform .2s ease; }
.snerp__why.is-open .snerp__why-chev { transform: rotate(180deg); }
.snerp__why-text {
  font-size: 13.3px;
  line-height: 1.6;
  color: rgba(203, 213, 225, .82);
  background: rgba(255, 255, 255, .03);
  border: 1px solid rgba(138, 180, 251, .14);
  border-radius: 11px;
  padding: 13px 15px;
  margin: 0 0 18px;
}
.snerp__why-text strong { color: #fff; font-weight: 600; }

.snerp__cta {
  width: 100%;
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  min-height: 56px;
  padding: 8px 18px;
  border: none;
  border-radius: 13px;
  background: linear-gradient(135deg, #2563EB, #1D4FD8);
  color: #fff;
  font-family: inherit;
  font-size: 15.5px;
  font-weight: 700;
  cursor: pointer;
  text-align: center;
  box-shadow: 0 14px 34px -10px rgba(37, 99, 235, .85), 0 0 0 1px rgba(124, 168, 247, .2) inset;
  transition: transform .12s ease, box-shadow .2s ease, filter .2s ease;
}
.snerp__cta:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.06);
  box-shadow: 0 18px 42px -10px rgba(37, 99, 235, .95), 0 0 0 1px rgba(124, 168, 247, .3) inset;
}
.snerp__cta:active { transform: translateY(0); }
.snerp__cta:disabled { opacity: .8; cursor: default; }
.snerp__cta svg { transition: transform .15s ease; }
.snerp__cta:hover:not(:disabled) svg { transform: translateX(3px); }
.snerp__cta--ghost {
  background: rgba(255, 255, 255, .05);
  border: 1px solid rgba(138, 180, 251, .28);
  box-shadow: none;
  color: #cfe0ff;
  min-height: 50px;
  font-size: 14.5px;
}
.snerp__cta--ghost:hover:not(:disabled) {
  background: rgba(37, 99, 235, .18);
  box-shadow: none;
}

.snerp__doclink {
  display: flex; align-items: center; justify-content: center; gap: 9px;
  margin-top: 14px;
  padding: 4px;
  font-size: 13.5px;
  color: rgba(148, 163, 184, .9);
  text-decoration: none;
  transition: color .15s;
}
.snerp__doclink svg { flex: none; color: #8AB4FB; }
.snerp__doclink strong { color: #8AB4FB; font-weight: 700; }
.snerp__doclink:hover { color: #cbd5e1; }
.snerp__doclink:hover strong { text-decoration: underline; }

.snerp__spin {
  width: 17px; height: 17px;
  border: 2.5px solid rgba(255,255,255,.35);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: snerp-spin .7s linear infinite;
}
@keyframes snerp-spin { to { transform: rotate(360deg); } }

.snerp__error {
  margin: 12px 0 0;
  font-size: 13.5px;
  color: #fca5a5;
  text-align: center;
}
.snerp__link {
  background: none; border: none; padding: 0;
  color: #8AB4FB; font: inherit; font-weight: 700; cursor: pointer;
  text-decoration: underline;
}

/* Painel PIX */
.snerp__pix {
  display: flex;
  gap: 18px;
  align-items: center;
  margin: 8px 0 4px;
  flex-wrap: wrap;
}
.snerp__qr {
  flex: none;
  width: 176px; height: 176px;
  border-radius: 14px;
  background: #fff;
  padding: 8px;
  box-shadow: 0 10px 30px -10px rgba(0,0,0,.6);
}
.snerp__pix-copy {
  flex: 1 1 220px;
  display: flex; flex-direction: column; gap: 8px;
}
.snerp__pix-label {
  font-size: 12px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  color: rgba(148, 163, 184, .9);
}
.snerp__pix-code {
  width: 100%;
  resize: none;
  border-radius: 10px;
  border: 1px solid rgba(138, 180, 251, .22);
  background: rgba(5, 8, 22, .5);
  color: #e2e8f0;
  font-family: 'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  padding: 10px 12px;
  word-break: break-all;
}
.snerp__copy {
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 11px;
  background: linear-gradient(135deg, #2563EB, #1D4FD8);
  color: #fff;
  font-family: inherit;
  font-size: 14.5px; font-weight: 700;
  cursor: pointer;
  transition: filter .15s;
}
.snerp__copy:hover { filter: brightness(1.08); }

.snerp__video {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  margin: 18px 0 20px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(138, 180, 251, .16);
  background: #060a18;
}
.snerp__video iframe,
.snerp__video video {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  border: none;
  display: block;
  object-fit: contain;
}
.snerp__video-ph {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px;
  background:
    radial-gradient(ellipse 60% 60% at 50% 40%, rgba(37, 99, 235, .18), transparent 70%),
    #070b1a;
}
.snerp__play {
  width: 64px; height: 64px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563EB, #1E40AF);
  color: #fff;
  padding-left: 4px;
  box-shadow: 0 10px 30px -8px rgba(37, 99, 235, .8), 0 0 0 8px rgba(37, 99, 235, .12);
}
.snerp__video-label {
  font-size: 13px; font-weight: 500;
  color: rgba(148, 163, 184, .9);
  letter-spacing: .02em;
}

.snerp__dismiss {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  margin: 4px 0 22px;
  font-size: 13.5px;
  color: rgba(148, 163, 184, .85);
  cursor: pointer;
  user-select: none;
}
.snerp__dismiss input {
  width: 16px; height: 16px;
  accent-color: #2563EB;
  cursor: pointer;
}
.snerp__dismiss:hover { color: rgba(203, 213, 225, .95); }

@keyframes snerp-fade { from { opacity: 0 } to { opacity: 1 } }
@keyframes snerp-pop {
  from { opacity: 0; transform: translateY(14px) scale(.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

@media (max-width: 520px) {
  .snerp { padding: 0; align-items: flex-end; }
  .snerp__card {
    width: 100%;
    height: auto;
    max-width: 100%;
    max-height: 94vh;
    border-radius: 22px 22px 0 0;
  }
  .snerp__urgency { flex-wrap: wrap; }
  .snerp__body { padding: 20px 20px 18px; }
  .snerp__title { font-size: 21px; }
  .snerp__pix { justify-content: center; }
  .snerp__qr { width: 200px; height: 200px; }
}
</style>
